import { EmailSchema } from "@iec-id/shared";
import { Button, Input, useOnline } from "@iec-id/ui";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
	type AuthSearch,
	maskEmail,
	pendingAuthorizeOf,
	readPendingSignIn,
	resolveService,
	safeExternalUrl,
	savePendingSignIn,
} from "@/lib/auth-flow";
import { parseEmailInput } from "@/lib/email-input";
import { api } from "@/lib/trpc";
import { AuthCard, AuthHeading, OfflineNotice, OrDivider } from "./auth-card";
import { GoogleMark } from "./brand-mark";
import { ServiceBadge } from "./service-badge";
import { useSigninLocale, useSigninT } from "./signin-i18n";

/** Сколько ждать до повторной отправки, если сервер не назвал срок сам. */
const DEFAULT_THROTTLE_SEC = 60;

/** Единственная проверка адреса — контрактная схема, второй копии правил нет. */
const isContractEmail = (value: string): boolean => EmailSchema.safeParse(value).success;

/**
 * Экран ввода почты (макет 4a, карточка 1).
 *
 * Отдельного действия «зарегистрироваться» нет и не появится: экран один и для
 * первого входа, и для сотого — поэтому в текстах не должно быть ни слова про
 * создание аккаунта. Человек вводит почту, дальше решает сервер.
 */
export function EmailStep({ search }: { search: AuthSearch }) {
	const t = useSigninT();
	const locale = useSigninLocale();
	const online = useOnline();
	const navigate = useNavigate();
	const service = resolveService(search);

	const [requestError, setRequestError] = useState<string | null>(null);

	const requestCode = api.auth.requestEmailCode.useMutation({
		onSuccess: (data, variables) => {
			setRequestError(null);
			const now = Date.now();
			savePendingSignIn({
				email: variables.email,
				maskedEmail: data.maskedEmail,
				expiresAt: now + data.expiresInSec * 1000,
				resendAt: now + data.resendAfterSec * 1000,
			});
			void navigate({ to: "/code", search });
		},
		onError: (error, variables) => {
			// 429 значит «код уже улетел». Возвращать человека к пустому полю в
			// этот момент вредно: письмо у него уже есть, ему нужен экран кода —
			// и уходим туда в ОБОИХ случаях, а не только когда сохранился
			// `pending` (аудит 2026-08-20, В11: без снимка экран оставался на
			// почте с таймером, хотя комментарий обещал переход).
			if (error.data?.httpStatus === 429) {
				const pending = readPendingSignIn();
				if (!pending || pending.email !== variables.email) {
					savePendingSignIn({
						email: variables.email,
						maskedEmail: maskEmail(variables.email),
						// Срока не знаем: письмо ушло раньше и не от нас. См.
						// `PendingSignIn.expiresAt` — выдуманное число врёт на экране.
						expiresAt: null,
						resendAt: Date.now() + DEFAULT_THROTTLE_SEC * 1000,
					});
				}
				void navigate({ to: "/code", search });
				return;
			}
			setRequestError(t("email.errors.failed"));
		},
	});

	const googleStart = api.auth.googleStart.useMutation({
		onSuccess: (data) => {
			// Уходим на accounts.google.com целиком: OAuth-редирект нельзя выполнить
			// внутри SPA, а `state` сервер уже положил в свою сессию.
			window.location.assign(data.authorizationUrl);
		},
		onError: () => setRequestError(t("email.googleFailed")),
	});

	const form = useForm({
		defaultValues: { email: "" },
		onSubmit: async ({ value }) => {
			setRequestError(null);
			// Адрес уезжает на сервер в ASCII-форме: контракт (`z.email()`) юникода
			// не принимает, а `пример@почта.рф` — валидный адрес (В5).
			const parsed = parseEmailInput(value.email, isContractEmail);
			if (parsed.state !== "ok") return;
			await requestCode.mutateAsync({
				email: parsed.value,
				purpose: "sign_in",
				locale,
				...(service ? { serviceId: service.id } : {}),
				// Реферальный код обязан уехать и здесь, а не только в Google-ветке:
				// человек, пришедший по `/ref/<code>` и выбравший почту, иначе
				// регистрировался без связи с пригласившим — ни ему приветственных
				// Баллов, ни начисления инвайтеру (аудит 2026-08-20, К1).
				...(search.ref ? { referralCode: search.ref } : {}),
			});
		},
	});

	const busy = requestCode.isPending || googleStart.isPending;

	return (
		<AuthCard>
			{/*
			  Куда человек входит — в заголовке, а не мелкой подписью: он пришёл
			  из StudyHub или Hero и должен видеть, что попал туда, куда шёл.
			  Знак сервиса берётся ИЗ РЕЕСТРА по `service.id` — принимать его
			  параметром запроса нельзя, см. `service-badge.tsx`.
			*/}
			<AuthHeading
				title={service ? t("email.titleService", { service: service.name }) : t("email.title")}
				subtitle={t("email.subtitle")}
				{...(service ? { badge: <ServiceBadge service={service} /> } : {})}
			/>

			<OfflineNotice />

			<Button
				variant="second"
				fullWidth
				iconLeft={<GoogleMark />}
				state={googleStart.isPending ? "loading" : !online || busy ? "disabled" : "default"}
				onClick={() => {
					setRequestError(null);
					const redirectTo = safeExternalUrl(search.redirect_to);
					// Контекст authorize уезжает вместе с человеком: Google — это
					// уход с нашего домена целиком, и без него сервис получал
					// вернувшегося гостя вместо кода (см. `pendingAuthorizeOf`).
					const authorize = pendingAuthorizeOf(search);
					googleStart.mutate({
						...(redirectTo ? { redirectTo } : {}),
						...(service ? { serviceId: service.id } : {}),
						...(search.ref ? { referralCode: search.ref } : {}),
						...(authorize ? { authorize } : {}),
					});
				}}
			>
				{t("email.google")}
			</Button>

			<OrDivider label={t("email.or")} />

			<form
				noValidate
				className="flex flex-col gap-5"
				onSubmit={(event) => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<form.Field
					name="email"
					validators={{
						onSubmit: ({ value }) => {
							const trimmed = value.trim();
							if (trimmed.length === 0) return t("email.errors.required");
							const parsed = parseEmailInput(trimmed, isContractEmail);
							if (parsed.state === "ok") return undefined;
							// «Неверный адрес» на верном адресе — тупик: человек не знает,
							// что именно чинить. Про юникод до «@» говорим отдельно.
							return parsed.state === "unicode-local"
								? t("email.errors.unicodeLocal")
								: t("email.errors.invalid");
						},
					}}
				>
					{(field) => {
						const message = field.state.meta.errors.find(
							(item): item is string => typeof item === "string",
						);
						return (
							<Input
								label={t("email.field")}
								type="email"
								name="email"
								autoComplete="email"
								inputMode="email"
								enterKeyHint="go"
								autoCapitalize="none"
								spellCheck={false}
								// Фокус на email не ставим: основной путь — Google, а автофокус
								// сразу открывает автозаполнение поверх кнопки.
								value={field.state.value}
								disabled={busy}
								onChange={(event) => field.handleChange(event.target.value)}
								onBlur={field.handleBlur}
								{...(message ? { errorText: message } : {})}
							/>
						);
					}}
				</form.Field>

				{requestError ? (
					<p role="alert" className="t-body text-danger">
						{requestError}
					</p>
				) : null}

				<form.Subscribe selector={(state) => state.values.email.trim().length > 0}>
					{(filled) =>
						// Кнопка появляется только когда есть что отправлять. В макете её нет
						// вовсе — там форма отправляется Enter'ом, но на телефоне это тупик:
						// экранная клавиатура даёт «Go», а внешней клавиатуры нет.
						filled ? (
							<Button
								type="submit"
								variant="primary"
								fullWidth
								state={requestCode.isPending ? "loading" : !online ? "disabled" : "default"}
							>
								{t("email.submit")}
							</Button>
						) : null
					}
				</form.Subscribe>
			</form>
		</AuthCard>
	);
}
